import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  MapPin, Search, Filter, ChevronDown, ChevronUp, Clock,
  DollarSign, Tag, Calendar, Eye, CheckCircle, Loader2,
  X, List, Map as MapIcon, AlertCircle, Zap
} from "lucide-react";

// Import Leaflet CSS
import "leaflet/dist/leaflet.css";

// Fix default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons for different statuses
const createCustomIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
};

const markerIcons = {
  new: createCustomIcon("#22c55e"),      // Green
  viewed: createCustomIcon("#3b82f6"),   // Blue
  expiring: createCustomIcon("#f97316"), // Orange
};

// Format relative time
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Map center control component
const MapCenterControl = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], zoom || 10);
    }
  }, [center, zoom, map]);
  
  return null;
};

// Lead Card Component for the sidebar
const LeadCard = ({ lead, isActive, onClick, onAccept, onView }) => {
  const statusColors = {
    new: "bg-emerald-100 text-emerald-700 border-emerald-200",
    viewed: "bg-blue-100 text-blue-700 border-blue-200",
    expiring: "bg-orange-100 text-orange-700 border-orange-200",
  };
  
  const statusLabels = {
    new: "New",
    viewed: "Viewed",
    expiring: "Expiring",
  };

  return (
    <div
      className={`p-3 rounded-lg border cursor-pointer transition-all ${
        isActive 
          ? "border-[#d4a853] bg-[#d4a853]/5 shadow-md" 
          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
      }`}
      onClick={onClick}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-sm text-[#1e3a5f] line-clamp-1">{lead.title}</h4>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${statusColors[lead.map_status]}`}>
          {statusLabels[lead.map_status]}
        </span>
      </div>
      
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <Tag className="w-3 h-3" />
        <span className="truncate">{lead.category}</span>
      </div>
      
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <MapPin className="w-3 h-3" />
        <span className="truncate">{lead.city || lead.location}</span>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {formatRelativeTime(lead.created_at)}
        </span>
        {lead.budget_max && (
          <span className="text-xs font-medium text-[#1e3a5f]">
            ${lead.budget_min || 0} - ${lead.budget_max}
          </span>
        )}
      </div>
      
      <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-7 text-xs"
          onClick={(e) => { e.stopPropagation(); onView(lead); }}
        >
          <Eye className="w-3 h-3 mr-1" />
          View
        </Button>
        <Button
          size="sm"
          className="flex-1 h-7 text-xs bg-[#1e3a5f] hover:bg-[#152a45]"
          onClick={(e) => { e.stopPropagation(); onAccept(lead); }}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Accept
        </Button>
      </div>
    </div>
  );
};

// Filter dropdown component
const FilterDropdown = ({ label, value, options, onChange, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
      >
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <span className="text-slate-700">{value || label}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[150px] py-1">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => { onChange(option.value); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                  value === option.value ? "bg-[#d4a853]/10 text-[#1e3a5f] font-medium" : "text-slate-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Empty state component
const EmptyState = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/10 backdrop-blur-[1px] z-[1000]">
    <Card className="w-80 shadow-xl border-0">
      <CardContent className="py-8 text-center">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <MapPin className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="font-semibold text-[#1e3a5f] mb-2">No leads in your area yet</h3>
        <p className="text-sm text-slate-500">We'll show them on the map as they come in.</p>
      </CardContent>
    </Card>
  </div>
);

// Main Component
export default function ProLeadsMapPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [mapZoom, setMapZoom] = useState(10);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [daysFilter, setDaysFilter] = useState("");
  const [radiusFilter, setRadiusFilter] = useState("");

  const fetchLeads = useCallback(async () => {
    if (!token) return;

    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append("category", categoryFilter);
      if (daysFilter) params.append("days", daysFilter);
      if (radiusFilter) params.append("radius", radiusFilter);

      const response = await axios.get(`${API}/pro/leads/map?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLeads(response.data.leads || []);
      setCategories(response.data.categories || []);
      if (response.data.center) {
        setMapCenter(response.data.center);
      }
      if (response.data.zoom) {
        setMapZoom(response.data.zoom);
      }
    } catch (error) {
      console.error("Failed to load leads map:", error);
      if (error.response?.status === 403) {
        toast.error("Subscribe to access the leads map");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [token, categoryFilter, daysFilter, radiusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setMapCenter({ lat: lead.latitude, lng: lead.longitude });
    setMapZoom(14);
  };

  const handleViewLead = (lead) => {
    navigate(`/job/${lead.id}`);
  };

  const handleAcceptLead = async (lead) => {
    try {
      await axios.post(`${API}/leads/${lead.id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lead accepted!");
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept lead");
    }
  };

  const daysOptions = [
    { value: "", label: "All Time" },
    { value: "1", label: "Last 24h" },
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
  ];

  const radiusOptions = [
    { value: "", label: "Any Distance" },
    { value: "5", label: "5 miles" },
    { value: "10", label: "10 miles" },
    { value: "25", label: "25 miles" },
    { value: "50", label: "50 miles" },
  ];

  const categoryOptions = [
    { value: "", label: "All Categories" },
    ...categories.map(c => ({ value: c, label: c }))
  ];

  // Count leads by status
  const newCount = leads.filter(l => l.map_status === "new").length;
  const viewedCount = leads.filter(l => l.map_status === "viewed").length;
  const expiringCount = leads.filter(l => l.map_status === "expiring").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="pro-leads-map-page">
      {/* Header with filters */}
      <div className="bg-white border-b border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-[#1e3a5f]">Leads Map</h1>
            <p className="text-sm text-slate-500">{leads.length} leads in your area</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <FilterDropdown
              label="Category"
              value={categoryFilter || "All Categories"}
              options={categoryOptions}
              onChange={setCategoryFilter}
              icon={Tag}
            />
            <FilterDropdown
              label="Time"
              value={daysOptions.find(o => o.value === daysFilter)?.label || "All Time"}
              options={daysOptions}
              onChange={setDaysFilter}
              icon={Calendar}
            />
            <FilterDropdown
              label="Distance"
              value={radiusOptions.find(o => o.value === radiusFilter)?.label || "Any Distance"}
              options={radiusOptions}
              onChange={setRadiusFilter}
              icon={MapPin}
            />
          </div>
        </div>
        
        {/* Status legend */}
        <div className="flex items-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span className="text-slate-600">New ({newCount})</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
            <span className="text-slate-600">Viewed ({viewedCount})</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            <span className="text-slate-600">Expiring ({expiringCount})</span>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex relative">
        {/* Sidebar - Desktop */}
        <div className={`hidden lg:block transition-all duration-300 ${sidebarOpen ? "w-80" : "w-0"} border-r border-slate-200 bg-white overflow-hidden`}>
          <div className="h-full flex flex-col">
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <span className="font-medium text-sm text-[#1e3a5f]">Lead List</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {leads.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No leads available
                </div>
              ) : (
                leads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    isActive={selectedLead?.id === lead.id}
                    onClick={() => handleLeadClick(lead)}
                    onAccept={handleAcceptLead}
                    onView={handleViewLead}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar toggle - Desktop */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-[1000] w-8 h-16 bg-white border border-slate-200 border-l-0 rounded-r-lg items-center justify-center shadow-md hover:bg-slate-50"
          >
            <List className="w-4 h-4 text-slate-600" />
          </button>
        )}

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={mapZoom}
            className="h-full w-full z-0"
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapCenterControl center={mapCenter} zoom={mapZoom} />
            
            {leads.map((lead) => (
              <Marker
                key={lead.id}
                position={[lead.latitude, lead.longitude]}
                icon={markerIcons[lead.map_status] || markerIcons.new}
                eventHandlers={{
                  click: () => setSelectedLead(lead),
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">{lead.title}</h3>
                    <div className="space-y-1 text-sm text-slate-600 mb-3">
                      <div className="flex items-center gap-2">
                        <Tag className="w-3 h-3" />
                        <span>{lead.category}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>{lead.city || lead.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        <span>{formatRelativeTime(lead.created_at)}</span>
                      </div>
                      {lead.budget_max && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-3 h-3" />
                          <span>${lead.budget_min || 0} - ${lead.budget_max}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs"
                        onClick={() => handleViewLead(lead)}
                      >
                        View Lead
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 h-8 text-xs bg-[#1e3a5f] hover:bg-[#152a45]"
                        onClick={() => handleAcceptLead(lead)}
                      >
                        Accept
                      </Button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Empty state overlay */}
          {leads.length === 0 && <EmptyState />}
        </div>

        {/* Mobile slide-up panel */}
        <div className={`lg:hidden absolute bottom-0 left-0 right-0 bg-white border-t border-slate-200 rounded-t-2xl shadow-lg z-[1000] transition-all duration-300 ${
          mobileListOpen ? "h-[60vh]" : "h-20"
        }`}>
          <button
            onClick={() => setMobileListOpen(!mobileListOpen)}
            className="w-full p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-[#1e3a5f]" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#1e3a5f]">{leads.length} Leads</p>
                <p className="text-xs text-slate-500">Tap to view list</p>
              </div>
            </div>
            {mobileListOpen ? (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {mobileListOpen && (
            <div className="px-4 pb-4 overflow-y-auto h-[calc(60vh-80px)] space-y-3">
              {leads.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No leads available
                </div>
              ) : (
                leads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    isActive={selectedLead?.id === lead.id}
                    onClick={() => {
                      handleLeadClick(lead);
                      setMobileListOpen(false);
                    }}
                    onAccept={handleAcceptLead}
                    onView={handleViewLead}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
